import {GripVertical, Plus, X} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {Textarea} from 'src/inputs/Textarea';
import {TextInput} from 'src/inputs/TextInput';

export type FieldValueEntry = {
  value: string;
  description: string;
};

type Props = {
  values: FieldValueEntry[];
  onChange: (values: FieldValueEntry[]) => void;
  onError: (error: string | null) => void;
};

export const FieldValuesInput = ({values, onChange, onError}: Props) => {
  const [newValue, setNewValue] = React.useState('');
  const [newValueDescription, setNewValueDescription] = React.useState('');

  const handleAddValue = () => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    if (trimmed.includes(':')) {
      onError('Values cannot contain colon (:) character');
      return;
    }
    if (values.some((v) => v.value === trimmed)) {
      onError('Value already exists');
      return;
    }
    onChange([
      ...values,
      {
        value: trimmed,
        description: newValueDescription.trim(),
      },
    ]);
    setNewValue('');
    setNewValueDescription('');
    onError(null);
  };

  const handleRemoveValue = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  const handleUpdateValue = (
    index: number,
    field: 'value' | 'description',
    newVal: string,
  ) => {
    onChange(
      values.map((v, i) => (i === index ? {...v, [field]: newVal} : v)),
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium">Values</label>
      <p className="text-muted-foreground text-xs">
        Define the possible values for this field
      </p>

      {values.length > 0 && (
        <div className="border-border mt-2 flex flex-col gap-3 rounded-md border p-3">
          {values.map((v, index) => (
            <div key={index} className="flex gap-2">
              <div className="text-muted-foreground flex items-start pt-2">
                <GripVertical className="h-4 w-4" />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <TextInput
                  value={v.value}
                  onChange={(val) => handleUpdateValue(index, 'value', val)}
                  placeholder="Value name"
                />
                <Textarea
                  value={v.description}
                  onChange={(val) =>
                    handleUpdateValue(index, 'description', val)
                  }
                  placeholder="Description (for LLM)"
                  rows={2}
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveValue(index)}
                className="text-muted-foreground hover:text-destructive pt-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="border-border mt-2 flex flex-col gap-2 rounded-md border border-dashed p-3">
        <div className="text-muted-foreground text-xs font-medium">
          Add new value
        </div>
        <TextInput
          value={newValue}
          onChange={setNewValue}
          placeholder="Value name..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddValue();
            }
          }}
        />
        <Textarea
          value={newValueDescription}
          onChange={setNewValueDescription}
          placeholder="Description (for LLM)..."
          rows={2}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.metaKey) {
              e.preventDefault();
              handleAddValue();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleAddValue}
          disabled={!newValue.trim()}
          className="self-start"
        >
          <Plus className="mr-1 h-4 w-4" />
          Add Value
        </Button>
      </div>
    </div>
  );
};
